import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import { sortedPosts } from "@/data/blog";

const SITE_URL = "https://episodekit.net";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function BlogIndex() {
  const posts = sortedPosts();

  useSEO({
    title:
      "Podcast SEO + Distribution Blog — Learn How to Rank Podcasts | EpisodeKit",
    description:
      "Editorial, founder-led writing on podcast SEO, transcripts, repurposing, AI search visibility, and content distribution. Built by the team at EpisodeKit.",
    path: "/blog",
    image: posts[0]?.seo?.ogImage,
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="blog-index">
      <Navbar />
      <main className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-neutral-500 mb-8 flex items-center gap-2"
          >
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-neutral-300">Blog</span>
          </nav>

          <header className="mb-14 md:mb-16 max-w-3xl">
            <p className="text-purple-400 uppercase text-xs tracking-widest mb-4 font-semibold">
              EpisodeKit Blog
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-unbounded font-bold leading-[1.05] mb-6">
              Podcast SEO &amp; distribution, written by the EpisodeKit team.
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed">
              Founder-led writing on how to make podcasts discoverable on
              Google, YouTube, and AI search. No fluff, no template content —
              every post is grounded in real workflow and real EpisodeKit
              outputs.
            </p>
          </header>

          <section
            aria-label="All posts"
            className="space-y-6"
            data-testid="blog-post-list"
          >
            {posts.map((post) => (
              <article
                key={post.slug}
                data-testid={`blog-card-${post.slug}`}
                className="group rounded-2xl border border-neutral-800 hover:border-purple-500/40 bg-gradient-to-b from-[#15131F] to-[#0E0D17] transition-colors overflow-hidden"
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="grid md:grid-cols-[280px_1fr] gap-0"
                >
                  <div className="relative bg-[#0A0913] p-6 flex items-center justify-center">
                    <img
                      src={post.cover.src}
                      alt={post.cover.alt}
                      loading="lazy"
                      className="w-full max-w-[220px] rounded-lg shadow-xl ring-1 ring-purple-500/10 group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 md:p-8 flex flex-col">
                    <div className="flex items-center flex-wrap gap-2 mb-4">
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide text-purple-200 bg-purple-500/15 border border-purple-500/30 uppercase">
                        {post.category}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(post.publishedAt)}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                        <Clock className="w-3.5 h-3.5" />
                        {post.readingTime}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-[28px] font-unbounded font-bold mb-3 leading-tight group-hover:text-white">
                      {post.title}
                    </h2>
                    <p className="text-neutral-400 leading-relaxed mb-6">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-purple-300 group-hover:text-white transition-colors">
                        Read article
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </section>

          <section className="mt-20 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-unbounded font-semibold mb-4">
              Want to apply this to your own podcast?
            </h2>
            <p className="text-neutral-400 mb-7">
              Upload one episode and inspect the full deliverable EpisodeKit
              generates — transcript, blog post, chapters, social, YouTube
              outputs — before you sign up.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/register">
                <button className="btn-primary inline-flex items-center gap-2 text-base px-8 py-4">
                  Try EpisodeKit Free
                  <ArrowRight className="w-5 h-5" />
                </button>
              </Link>
              <Link
                to="/episode-examples"
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-700 hover:border-purple-500/60 rounded-full px-5 py-3 transition-colors"
              >
                See a real deliverable
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// Helper for OG/JSON-LD usage by detail page — kept here to avoid an extra
// helper file.
export const SITE = SITE_URL;
